import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import ExclusiveDelivery from "@/components/ExclusiveDelivery";
import CommercialReadiness from "@/components/CommercialReadiness";

const inputStyle = "mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm";
const buttonStyle = "rounded-md border px-3 py-2 text-sm font-medium disabled:opacity-40";
const buyerInitial = {legal_name:"",contact_email:"",license_number:"",state:"FL",contract_reference:""};
const packageInitial = {name:"Five exclusive Final Expense inquiries",quantity:"5",unit_price_cents:"2500"};

export default function BuyerMarketplace() {
  const [buyers,setBuyers] = useState([]);
  const [orders,setOrders] = useState([]);
  const [packages,setPackages] = useState([]);
  const [leads,setLeads] = useState([]);
  const [capabilities,setCapabilities] = useState({checkout_ready:false});
  const [buyerForm,setBuyerForm] = useState(buyerInitial);
  const [packageForm,setPackageForm] = useState(packageInitial);
  const [buyerId,setBuyerId] = useState("");
  const [packageId,setPackageId] = useState("");
  const [licenseRef,setLicenseRef] = useState("");
  const [checkoutUrl,setCheckoutUrl] = useState("");
  const [busy,setBusy] = useState(false);
  const [error,setError] = useState("");
  const [notice,setNotice] = useState("");
  const [loading,setLoading] = useState(true);
  const [commercialReview,setCommercialReview] = useState(null);

  const refresh = useCallback(async () => {
    const [b,o,l,cap] = await Promise.all([
      api.get("/platform/lead-buyers"),
      api.get("/platform/lead-buyers/orders"),
      api.get("/platform/lead-marketplace/leads"),
      api.get("/platform/lead-buyers/capabilities").catch(() => ({data:{checkout_ready:false}}))
    ]);
    setBuyers(b.data || []);
    setOrders(o.data || []);
    setLeads(l.data || []);
    setCapabilities(cap.data || {checkout_ready:false});
    setBuyerId(current => current || (b.data || [])[0]?.id || "");
  },[]);

  useEffect(() => {refresh().catch(()=>setError("Unable to load buyer marketplace.")).finally(()=>setLoading(false));},[refresh]);
  useEffect(() => {
    if (!buyerId) { setPackages([]);setPackageId("");return; }
    let active=true;
    api.get(`/platform/lead-buyers/${buyerId}/packages`).then(({data})=>{
      if(!active)return;
      setPackages(data || []);setPackageId((data || [])[0]?.id || "");
    }).catch(()=>{if(active){setPackages([]);setPackageId("");}});
    return ()=>{active=false;};
  },[buyerId]);

  const selectedBuyer=buyers.find(x=>x.id===buyerId);
  const selectedPackages=packages;
  const alertError=(e)=>formatApiErrorDetail(e?.response?.data?.detail)||"Request failed.";
  const run = async (task, success) => {
    if(busy)return;
    setBusy(true);setError("");setNotice("");
    try {
      const outcome=await task();
      setNotice(success);await refresh();
      return outcome;
    } catch(e) {
      setError(alertError(e));
    } finally {setBusy(false);}
  };

  const createBuyer=async event=>{
    event.preventDefault();
    const result=await run(()=>api.post("/platform/lead-buyers",buyerForm),"Buyer created. Verify license and contract before accepting orders.");
    if(result){setBuyerForm(buyerInitial);setBuyerId(result.data.id);}
  };
  const verifyBuyer=()=>run(()=>api.post(`/platform/lead-buyers/${buyerId}/verify`,{
    verified_for_fl:true,review_reference:licenseRef
  }),"Buyer verification recorded. This attests to an external licensing review.");
  const createPackage=async event=>{
    event.preventDefault();
    const result=await run(()=>api.post(`/platform/lead-buyers/${buyerId}/packages`,{
      name:packageForm.name,quantity:Number(packageForm.quantity),
      unit_price_cents:Number(packageForm.unit_price_cents)
    }),"Prepaid package created.");
    if(result){setPackages(current=>[...current,result.data]);setPackageId(result.data.id);}
  };
  const createOrder=()=>run(()=>api.post(`/platform/lead-buyers/${buyerId}/orders`,{package_id:packageId}),
    "Order created. No funds have been received.");
  const checkout=async order=>{
    if(!capabilities.checkout_ready) {
      setError("Stripe checkout is disabled until the payment integration is fully configured.");
      return;
    }
    if(!window.confirm("Create a Stripe checkout link for this fixed-price order? Creating a link does not charge anyone. Payment is credited only after Stripe confirms it."))return;
    const result=await run(()=>api.post(`/platform/lead-buyers/orders/${order.id}/checkout`),
      "Stripe checkout link created. Share it only with the verified buyer.");
    if(result)setCheckoutUrl(result.data.checkout_url || "");
  };
  const reserve=async(order,lead)=>{
    if(!window.confirm("Reserve this eligible inquiry exclusively for the named buyer? No contact details will be delivered by this action."))return;
    await run(()=>api.post(`/platform/lead-buyers/orders/${order.id}/reserve/${lead.id}`),
      "Exclusive reservation recorded. Fulfillment is available below for verified buyers with an active internal agency.");
  };

  const textField=(label,value,update,props={})=>(
    <label className="block text-sm">{label}<input {...props} className={inputStyle} value={value} onChange={e=>update(e.target.value)}/></label>
  );
  if(loading)return <div className="p-6">Loading buyer marketplace…</div>;
  return <main className="mx-auto max-w-7xl space-y-6 p-2" data-testid="buyer-marketplace-page">
    <header>
      <h1 className="font-heading text-2xl font-bold">Prepaid Lead Buyers</h1>
      <p className="text-sm text-muted-foreground">
        Owner-only. License verification, prepaid orders and exclusive reservations. No automatic lead delivery or advertising. Internal-agency delivery requires independent identity and consent review.
      </p>
    </header>
    {error&&<p className="rounded-md border p-3 text-sm text-destructive" role="alert">{error}</p>}
    {notice&&<p className="rounded-md border p-3 text-sm" role="status">{notice}</p>}
    <div className="grid gap-5 lg:grid-cols-2">
      <form className="space-y-3 rounded-xl border bg-card p-5" onSubmit={createBuyer}>
        <h2 className="font-semibold">1. Register a buyer</h2>
        <p className="text-xs text-muted-foreground">Record a real agency and its signed buyer agreement. Buyer details remain private.</p>
        {textField("Full legal agency name",buyerForm.legal_name,v=>setBuyerForm(x=>({...x,legal_name:v})),{required:true})}
        {textField("Contact email",buyerForm.contact_email,v=>setBuyerForm(x=>({...x,contact_email:v})),{required:true,type:"email"})}
        {textField("Florida license number",buyerForm.license_number,v=>setBuyerForm(x=>({...x,license_number:v})),{required:true})}
        {textField("Signed agreement reference",buyerForm.contract_reference,v=>setBuyerForm(x=>({...x,contract_reference:v})),{required:true})}
        <button type="submit" disabled={busy} className={buttonStyle}>Register buyer</button>
      </form>
      <section className="space-y-3 rounded-xl border bg-card p-5">
        <h2 className="font-semibold">2. Verify buyer and offer</h2>
        <label className="block text-sm">Agency<select aria-label="Buyer agency" className={inputStyle} value={buyerId}
          onChange={e=>setBuyerId(e.target.value)}>
          <option value="">Select buyer</option>
          {buyers.map(b=><option key={b.id} value={b.id}>{b.legal_name} · {b.status}</option>)}
        </select></label>
        {selectedBuyer?.status!=="verified"&&buyerId&&<>
          <p className="text-xs text-muted-foreground">Check the Florida licensing authority, legal entity and signed agreement before attesting verification. This form does not perform an automatic license check.</p>
          {textField("External license review reference",licenseRef,setLicenseRef,{required:true})}
          <button type="button" className={buttonStyle} disabled={busy||licenseRef.trim().length<5}
            onClick={verifyBuyer}>Confirm buyer reviewed</button>
        </>}
        {selectedBuyer?.status==="verified"&&<>
          <div className="text-sm">Verified review: {selectedBuyer.verification_reference}</div>
          <CommercialReadiness key={`${buyerId}:${orders.length}`} buyerId={buyerId} onReview={setCommercialReview} />
          <form className="space-y-3" onSubmit={createPackage}>
            {textField("Package name",packageForm.name,v=>setPackageForm(x=>({...x,name:v})),{required:true})}
            {textField("Number of exclusive leads",packageForm.quantity,v=>setPackageForm(x=>({...x,quantity:v})),{type:"number",min:1,max:100,required:true})}
            {textField("Price per lead (USD cents)",packageForm.unit_price_cents,v=>setPackageForm(x=>({...x,unit_price_cents:v})),{type:"number",min:100,max:100000,required:true})}
            <button type="submit" className={buttonStyle} disabled={busy}>Create package</button>
          </form>
          <label className="block text-sm">Prepaid package
            <select className={inputStyle} value={packageId} onChange={e=>setPackageId(e.target.value)}>
              <option value="">Select package</option>
              {selectedPackages.map(p=><option key={p.id} value={p.id}>{p.name} · {p.quantity} at ${(p.unit_price_cents/100).toFixed(2)}/lead</option>)}
            </select>
          </label>
          <button className={buttonStyle} type="button" disabled={!packageId||busy||!commercialReview||new Date(commercialReview.expires_at)<=new Date()||commercialReview.remaining_capacity<Number(selectedPackages.find(p=>p.id===packageId)?.quantity||1)} onClick={createOrder}>Create prepaid order</button>
          {!capabilities.checkout_ready && <p className="text-xs text-muted-foreground" data-testid="checkout-not-ready">
            Payments are not yet enabled. Orders require a current commercial evidence review; buyers cannot pay until Stripe checkout is configured.
          </p>}
        </>}
      </section>
    </div>
    {checkoutUrl&&<div className="space-y-2 rounded-xl border p-4">
      <h2 className="font-semibold">Stripe checkout link</h2>
      <a href={checkoutUrl} target="_blank" rel="noreferrer" className="block break-all text-sm underline">{checkoutUrl}</a>
      <button type="button" className={buttonStyle} onClick={()=>navigator.clipboard?.writeText(checkoutUrl)}>Copy link</button>
      <p className="text-xs text-muted-foreground">A checkout link is not evidence of payment. Only a verified Stripe event funds the order.</p>
    </div>}
    <section className="space-y-3 rounded-xl border bg-card p-5">
      <h2 className="font-semibold">3. Prepaid orders</h2>
      {!orders.length&&<p className="text-sm text-muted-foreground">No buyer orders yet.</p>}
      {orders.map(order=>{
        const buyer=buyers.find(x=>x.id===order.buyer_id);
        return <div className="space-y-2 rounded-md border p-3 text-sm" key={order.id}>
          <div><strong>{buyer?.legal_name||"Agency"}</strong> · {order.package_name}</div>
          <div>${(order.amount_cents/100).toFixed(2)} · {order.status} · {order.remaining_quantity} prepaid slots available</div>
          {order.status==="awaiting_payment"&&order.checkout_state==="not_started"&&
            (capabilities.checkout_ready
              ? <button className={buttonStyle} type="button" disabled={busy} onClick={()=>checkout(order)}>Create Stripe checkout link</button>
              : <p className="text-xs text-muted-foreground">Payment link unavailable until the owner enables and configures Stripe Checkout.</p>)}
          {order.status==="paid"&&<div className="space-y-2">
            <p className="text-xs text-muted-foreground">Only consumers who specifically authorized this agency can be reserved. Reservation does not transmit PII.</p>
            {leads.filter(l=>l.status==="new"&&l.qualification_status==="qualified"&&l.sale_eligibility==="awaiting_buyer_verification"
              &&l.consent?.sharing_allowed===true
              &&(l.consent?.allowed_buyer_names||[]).includes(buyer?.legal_name)).slice(0,20).map(lead=>
                <button key={lead.id} className={buttonStyle+" mr-2"} disabled={busy||order.remaining_quantity===0}
                  onClick={()=>reserve(order,lead)}>Reserve {lead.id.slice(0,8)}</button>)}
          </div>}
        </div>;
      })}
    </section>
    <ExclusiveDelivery />
    <p className="text-xs text-muted-foreground">External buyer delivery and self-service require a separate secure integration. Licensing, disclosures and consumer contact permissions need independent review.</p>
  </main>;
}

